import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import UploadIcon from '@mui/icons-material/Upload';

import {
  StyledTab,
  StyledTabs,
  StyledTabsWrapper,
  StyledModal,
  StyledOverlay,
  useHomeStyles,
} from './styles';

import ScheduleCalendar from './ScheduleCalendar';

import Schedule from './Schedule';
import AccountSettings from './AccountSettings';
import ScheduleCardDetails from './ScheduleCardDetails';
import Header from './Header';
import SessionSummaryModal from './SessionSummaryModal';
import AvailabilitySettings from './AvailabilitySettings';
import Reports from './Reports';
import HomeOwners from './HomeOwners';
import DocumentTemplates from './DocumentTemplates';
import PlansModal from './PlansModal';
import FormLibrary from './FormLibrary';

import ManageReportModal from '../../components/ManageReportModal';
import EmptySchedule from '../../components/EmptySchedule';

import { COLORS } from '../../utils/colors';
import { removeItem } from '../../utils/helpers';

import { useBookingsContext } from '../../contexts/BookingsContext';
import useSession from '../../hooks/useSession';
import toggleToast from '../../utils/messaging';

type TabValues = 'active' | 'archived';
interface TabPanelProps {
  children?: React.ReactNode;
  index: TabValues;
  value: TabValues;
}

const CustomTabPanel = ({
  children,
  value,
  index,

  ...other
}: TabPanelProps) => {
  return (
    <Box id={`tab-option-${index}`} {...other}>
      {value === index && (
        <Box padding={'2.25rem'} display="flex">
          {children}
        </Box>
      )}
    </Box>
  );
};

const Home = () => {
  const styles = useHomeStyles();
  const {
    setIsBookingModalOpen,
    setBookingModalUrl,
    selectedBooking,
    setSelectedBooking,
    setEditBooking,
    gitHash,
    profile,
    organization,
    userSettings,
    isLoading,
    homeView,
    setHomeView,
    bookingListType,
    setBookingListType,
    groupedBookings,
    isFetchingBookings,
    plans,
    setPlans,
    updatePlan,
    documentTemplates,
    setDocumentTemplates,
    updateDocumentTemplate,
  } = useBookingsContext();

  const { restoreSession } = useSession();

  const [tabValue, setTabValue] = useState<TabValues>('active');

  const [isAccountModalOpen, setIsAccountModalOpen] = useState<boolean>(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] =
    useState<boolean>(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] =
    useState<boolean>(false);
  const [isDocumentTemplatesModalOpen, setIsDocumentTemplatesModalOpen] =
    useState<boolean>(false);
  const [isFormLibraryModalOpen, setIsFormLibraryModalOpen] =
    useState<boolean>(false);
  const [isPlansModalOpen, setIsPlansModalOpen] = useState<boolean>(false);
  const [details, setDetails] = useState<any>();
  const [openSummaryModal, setOpenSummaryModal] = useState<boolean>(false);

  const [menuHeight, setMenuHeight] = useState(0);
  useEffect(() => {
    console.log('::::useEffect userSettings home...', userSettings);
    setMenuHeight(
      (userSettings?.dev ? 15 : 9.25) + (userSettings?.supervisor ? 2.75 : 0),
    );
  }, [userSettings]);

  const handleTabSelect = (
    _event: React.SyntheticEvent,
    selectedTab: TabValues,
  ) => {
    console.log(`handleTabSelect: ${selectedTab}`);

    setBookingListType(selectedTab);
  };

  useEffect(() => {
    if (['active', 'archived'].includes(bookingListType))
      setTabValue(bookingListType as TabValues);
  }, [bookingListType]);

  useEffect(() => {
    if (homeView === 'calendar') {
      setBookingListType('month');
    } else if (homeView === 'schedule') {
      setBookingListType(tabValue);
    }
  }, [homeView]);

  const handleLogout = async () => {
    setIsSettingsModalOpen(false);
    removeItem('hp_auth_token');
    window.location.href = `${window._env_?.AUTH_APP_URL || process.env.REACT_APP_AUTH_APP_URL
      }`;
  };

  const maxAppHeight = 1250;
  const [fontSize, setFontSize] = useState(16);

  useEffect(() => {
    function handleResize() {
      if (window.innerHeight < 200 || window.innerWidth < 200) return;

      const maxScreenDimension =
        window.innerHeight > window.innerWidth
          ? window.innerHeight
          : window.innerWidth;
      const scale = maxScreenDimension / maxAppHeight;

      setFontSize(scale < 1 ? scale * 16 : 16);
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    //  applyApplicationFontSize(fontSize);
  }, [fontSize]);

  const handleClick = () => {
    setEditBooking(null);
    setBookingModalUrl('booking-spot');
    setIsBookingModalOpen(true);
  };

  return (
    <Box sx={styles.wrapper}>
      <Header
        handleOpenSettingsModal={() => setIsSettingsModalOpen(true)}
        homeView={homeView}
        setHomeView={setHomeView}
        userSettings={userSettings}
      />
      {homeView === 'schedule' && (
        <>
          <StyledTabsWrapper>
            <StyledTabs>
              <Tabs value={tabValue} onChange={handleTabSelect}>
                <StyledTab label="Active Cases" value="active" />
                <StyledTab label="Archived" value="archived" />
              </Tabs>
            </StyledTabs>
            <Box
              sx={{
                ml: 'auto',
                mr: '1rem',
                display: 'flex',
                alignItems: 'center',
              }}>
              <Tooltip title="Restore Archived Session">
                <IconButton
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.zip,application/zip';
                    input.onchange = async (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (!file) return;

                      if (file.type !== 'application/zip') {
                        console.error('Please upload a zip file');
                        return;
                      }

                      const formData = new FormData();
                      formData.append('archive', file);

                      await restoreSession({ formData });
                    };
                    input.click();
                  }}>
                  <UploadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </StyledTabsWrapper>

          {isFetchingBookings ? (
            <Box sx={styles.container}>
              <CircularProgress sx={{ color: COLORS.PRIMARY }} />
            </Box>
          ) : (
            <Box
              id="home-schedule-container"
              onClick={() => setSelectedBooking(null)}
              sx={{
                height: 'calc(100vh - 5.5rem)',
                overflow: 'auto',
              }}>
              <CustomTabPanel value={tabValue} index={'active'}>
                {groupedBookings.length ? (
                  <>
                    <ManageReportModal />
                    <Schedule />
                    {selectedBooking && <ScheduleCardDetails />}
                  </>
                ) : (
                  <EmptySchedule
                    emptyText="You have no current or upcoming bookings at the moment. Click the button below to update your availability."
                    handleClick={handleClick}
                  />
                )}
              </CustomTabPanel>

              <CustomTabPanel value={tabValue} index={'archived'}>
                {groupedBookings.length ? (
                  <>
                    <ManageReportModal />
                    <Schedule />
                    {selectedBooking && <ScheduleCardDetails tab="archived" />}
                  </>
                ) : (
                  <EmptySchedule
                    emptyText="You haven't attended any bookings yet. Click the button below to update your availability."
                    handleClick={handleClick}
                  />
                )}
              </CustomTabPanel>
            </Box>
          )}
        </>
      )}
      {homeView === 'calendar' && (
        <>
          <SessionSummaryModal
            open={openSummaryModal}
            onClose={() => setOpenSummaryModal(false)}
            details={details}
          />
          <Box
            display={'flex'}
            alignItems={'center'}
            sx={{ backgroundColor: '#fafafa' }}>
            <ScheduleCalendar firstVisit={false} />
          </Box>
        </>
      )}
      {homeView === 'reports' && <Reports />}
      {homeView === 'homeowners' && <HomeOwners />}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '2rem' }}>
          <CircularProgress sx={{ color: COLORS.PRIMARY }} />
        </Box>
      )}
      {!isLoading && (
        <>
          <AccountSettings
            gitHash={gitHash}
            organization={organization}
            profile={profile}
            userSettings={userSettings}
            isModalOpen={isAccountModalOpen}
            handleCloseModal={() => setIsAccountModalOpen(false)}
          />

          <AvailabilitySettings
            profile={profile}
            isModalOpen={isAvailabilityModalOpen}
            handleCloseModal={() => setIsAvailabilityModalOpen(false)}
          />

          <DocumentTemplates
            profile={profile}
            userSettings={userSettings}
            documentTemplates={documentTemplates}
            setDocumentTemplates={setDocumentTemplates}
            updateDocumentTemplate={updateDocumentTemplate}
            isModalOpen={isDocumentTemplatesModalOpen}
            handleCloseModal={() => setIsDocumentTemplatesModalOpen(false)}
          />

          <FormLibrary
            profile={profile}
            userSettings={userSettings}
            documentTemplates={documentTemplates}
            isModalOpen={isFormLibraryModalOpen}
            handleCloseModal={() => setIsFormLibraryModalOpen(false)}
          />

          <PlansModal
            profile={profile}
            plans={plans}
            setPlans={setPlans}
            updatePlan={updatePlan}
            isPlansModalOpen={isPlansModalOpen}
            handleClosePlansModal={() => setIsPlansModalOpen(false)}
          />
        </>
      )}
      <StyledOverlay
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}>
        <StyledModal sx={{ height: `${menuHeight}rem` }}>
          <Typography
            sx={styles.menuItem}
            onClick={() => {
              setIsAccountModalOpen(true);
              setIsSettingsModalOpen(false);
            }}>
            Account
          </Typography>
          {userSettings?.dev && (
            <Typography
              sx={styles.menuItem}
              onClick={() => {
                setIsAvailabilityModalOpen(true);
                setIsSettingsModalOpen(false);
              }}>
              Availability
            </Typography>
          )}
          {userSettings?.dev && (
            <Typography
              sx={styles.menuItem}
              onClick={() => {
                setIsPlansModalOpen(true);
                setIsSettingsModalOpen(false);
              }}>
              Plans
            </Typography>
          )}
          <Typography
            sx={styles.menuItem}
            onClick={() => {
              setIsDocumentTemplatesModalOpen(true);
              setIsSettingsModalOpen(false);
            }}>
            Document Templates
          </Typography>
          {userSettings?.supervisor && (
            <Typography
              sx={styles.menuItem}
              onClick={() => {
                setIsFormLibraryModalOpen(true);
                setIsSettingsModalOpen(false);
              }}>
              Forms
            </Typography>
          )}
          <Typography
            sx={{
              ...styles.logout,
              ...styles.menuItem,
            }}
            onClick={() => {
              handleLogout();
            }}>
            Log Out
          </Typography>
        </StyledModal>
      </StyledOverlay>
    </Box>
  );
};

export default Home;
